/**
 * NOTES-V2 N2 — card diferenciado por tipo.
 *
 * Base comum: glass-card glow-hover + border-left 3px colorida.
 * Variants sutis por tipo:
 *   idea       → tint amber, ícone +2, primeira linha destacada
 *   journal    → drop cap roxo (first-letter)
 *   vent       → border-left 5px (mais grosso), italic
 *   research   → URL extraída como chip clicável (hostname)
 *   daily_review → renderiza markdown completo (não clamp)
 *   general    → padrão (compat com NoteCard legado)
 *
 * Markdown via NoteMarkdown (sanitizado, XSS-safe). Cards comuns
 * usam clamp=5; daily_review clamp=8 (mais espaço).
 *
 * Telemetria: trace.record na expansão e delete.
 */
import { motion } from "framer-motion";
import { Trash2, ExternalLink, Pin } from "lucide-react";
import { NoteMarkdown } from "@/utils/notesMarkdown";
import {
  NOTE_TYPES,
  noteTypeOf,
  formatNoteTime,
} from "@/utils/noteGrouping";
import trace from "@/services/executionTrace";

const FIRST_URL_RE = /(https?:\/\/[^\s)]+)/i;

function _hostFromUrl(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function _hasMarkdown(content) {
  if (!content) return false;
  return /(^|\n)\s*(#{1,3}\s|[-*]\s|\d+\.\s|>\s)|(\*\*[^*]+\*\*|\[[^\]]+\]\()/.test(content);
}

export default function NoteCardDifferentiated({
  note,
  onDelete,
  onClick,
  variant = null,  // se null, deriva de note.type
}) {
  const typeKey = note.type && NOTE_TYPES[note.type] ? note.type : "general";
  const type = NOTE_TYPES[typeKey];
  const Icon = type.icon;
  const v = variant || typeKey;

  const renderMarkdown = (v === "daily_review" || v === "research" || _hasMarkdown(note.content));
  const clamp = v === "daily_review" ? 8 : v === "research" ? 6 : 5;

  // Research: tenta extrair primeira URL pra mostrar como chip.
  let researchUrl = null;
  let researchHost = null;
  if (v === "research" && note.content) {
    const m = FIRST_URL_RE.exec(note.content);
    if (m) {
      researchUrl = m[1];
      researchHost = _hostFromUrl(researchUrl);
    }
  }

  // Variants — classes condicionais.
  const baseCls = "glass-card glow-hover rounded-2xl p-5 cursor-pointer group flex flex-col relative overflow-hidden";
  const borderLeftWidth = v === "vent" ? 5 : 3;
  // Tint de fundo discreto pra idea.
  const variantBg = v === "idea" ? "bg-amber-500/[0.03]" : "";
  // Largura especial pra daily_review (span 3 colunas no grid).
  const widthCls = v === "daily_review" ? "md:col-span-2 lg:col-span-3" : "";

  const handleClick = () => {
    try {
      trace.record("notes.card.opened", {
        type: typeKey,
        has_url: !!researchUrl,
        has_markdown: renderMarkdown,
      });
    } catch {/* */}
    onClick?.(note);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    try {
      trace.record("notes.card.deleted", { type: typeKey });
    } catch {/* */}
    onDelete?.(note);
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      whileHover={{ y: -3 }}
      className={`${baseCls} ${variantBg} ${widthCls}`}
      onClick={handleClick}
      data-testid={`note-card-${note._id || note.id}`}
      data-variant={v}
      style={{
        borderLeft: `${borderLeftWidth}px solid ${type.color}`,
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div
          className="rounded-lg flex items-center justify-center"
          style={{
            backgroundColor: `${type.color}14`,
            width: v === "idea" ? 36 : 32,
            height: v === "idea" ? 36 : 32,
          }}
        >
          <Icon
            className={v === "idea" ? "w-5 h-5" : "w-4 h-4"}
            style={{ color: type.color }}
          />
        </div>
        <div className="flex items-center gap-1">
          {/* PRODUTO-V1 N6 — pin indicator (sempre visível quando pinned) */}
          {note.pinned && (
            <span
              className="p-1.5 text-amber-400"
              title="Fixada"
              data-testid={`note-pinned-${note._id || note.id}`}
            >
              <Pin className="w-3.5 h-3.5" />
            </span>
          )}
          <button
            onClick={handleDelete}
            className="p-1.5 rounded-lg text-slate-700 hover:text-red-400 hover:bg-red-500/10 transition-colors opacity-0 group-hover:opacity-100"
            data-testid={`delete-note-${note._id || note.id}`}
            aria-label="Excluir nota"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Research: chip de URL */}
      {researchUrl && researchHost && (
        <a
          href={researchUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 mb-2 max-w-fit hover:bg-blue-500/15 transition-colors"
          data-testid={`note-url-chip-${note._id || note.id}`}
        >
          <ExternalLink className="w-3 h-3" />
          {researchHost}
        </a>
      )}

      {/* Content */}
      <div className="flex-1 leading-relaxed">
        {renderMarkdown ? (
          <NoteMarkdown content={note.content} clamp={clamp} />
        ) : v === "journal" ? (
          <p
            className={`text-sm text-slate-300 line-clamp-${clamp} first-letter:text-3xl first-letter:font-display first-letter:text-purple-300 first-letter:mr-1 first-letter:float-left first-letter:leading-none`}
          >
            {note.content}
          </p>
        ) : v === "vent" ? (
          <p className={`text-sm text-slate-300 italic line-clamp-${clamp}`}>
            {note.content}
          </p>
        ) : v === "idea" && note.content ? (
          (() => {
            const lines = String(note.content).split("\n");
            const first = lines[0];
            const rest = lines.slice(1).join("\n");
            return (
              <>
                <p className="text-sm font-medium text-white line-clamp-2 mb-1">
                  {first}
                </p>
                {rest && (
                  <p className="text-sm text-slate-400 line-clamp-3">{rest}</p>
                )}
              </>
            );
          })()
        ) : (
          <p className={`text-sm text-slate-300 line-clamp-${clamp}`}>
            {note.content}
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.04]">
        <span
          className="px-2 py-0.5 rounded-md text-[10px] font-medium uppercase tracking-wider"
          style={{ backgroundColor: `${type.color}10`, color: type.color }}
        >
          {type.label}
        </span>
        <span className="text-[11px] text-slate-600">
          {formatNoteTime(note.created_at)}
        </span>
      </div>

      {/* Duplicatas */}
      {note.duplicatesHidden && note.duplicatesHidden > 0 && (
        <div
          className="mt-2 text-[11px] text-slate-500 italic"
          data-testid={`note-duplicates-${note._id || note.id}`}
        >
          + {note.duplicatesHidden} duplicata{note.duplicatesHidden > 1 ? "s" : ""} escondida{note.duplicatesHidden > 1 ? "s" : ""}
        </div>
      )}
    </motion.div>
  );
}
